// src/components/corkboard/FlowDiagram.jsx
//
// The board's second view: the same elements and relationships as the
// corkboard, laid out in columns and joined by curves instead of thread.
//
// **Four columns, where the prototype had three.** The prototype's model made a
// scene and a chapter the same thing, so three columns covered it. Here they
// are separate — a chapter is a heading in the manuscript, derived and never
// stored — and relationships point at chapters constantly. Dropping that column
// would silently hide a large part of the graph, so the manuscript gets a
// column of its own on the right, which is also how the relationships read:
// actors and artifacts flow through scenes into the book.
//
// **Positions are measured, never computed.** The columns are ordinary flow
// layout, so where a node ends up depends on its name wrapping, the font, and
// the width of the panel beside it. The curves are drawn from measured boxes
// after layout, which is the only way the two can agree. This is the same
// lesson as the editor's ruler: measure, don't predict.
//
// Nothing here writes. Clicking a node selects it, and the right rail — the
// same panel the corkboard uses — is where it is edited.
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { flowLines } from '../../utils/corkBoard';
import { CB, FONT, TYPE_COLOR, BP } from './corkTokens';

// Left to right: the elements, then the book they end up in. Actor / scene /
// artifact is the same order the corkboard's fallback layout uses, so switching
// views does not rearrange an author's mental map of the board.
const COLUMNS = [
  { key: 'actor', label: 'Actors' },
  { key: 'scene', label: 'Scenes' },
  { key: 'artifact', label: 'Artifacts' },
  { key: 'chapter', label: 'Manuscript' },
];

const NODE_SKIN = {
  actor: { bg: '#F2EFFC', border: '#DAD3F3' },
  scene: { bg: '#FBF5E4', border: '#EFE3BE' },
  artifact: { bg: '#FBEEF2', border: '#EFCFD9' },
  chapter: { bg: CB.lavender, border: CB.lineSoft },
};

export default function FlowDiagram({
  nodes,
  edges,
  chapters,
  selectedId,
  showAllConnections,
  onSelect,
  emptyState,
}) {
  const stageRef = useRef(null);
  const nodeRefs = useRef(new Map());
  const [measured, setMeasured] = useState(new Map());
  // The SVG has to cover the scrolled content, not the visible box, so its size
  // is state rather than a read of stageRef during render — a ref read would be
  // null on the first paint and would never trigger the re-render that fixes it.
  const [canvas, setCanvas] = useState({ w: 0, h: 0 });

  const selectedRef = selectedId ? `node:${selectedId}` : null;
  const focusRef = showAllConnections ? null : selectedRef;

  const columns = useMemo(() => {
    const byType = { actor: [], scene: [], artifact: [] };
    nodes.forEach((n) => { (byType[n.type] || (byType[n.type] = [])).push(n); });
    return {
      ...byType,
      // Chapters are not nodes, so they are shaped into the same {ref, name}
      // form here rather than the renderer learning about two kinds of thing.
      chapter: chapters.map((c) => ({
        _id: c.blockId,
        type: 'chapter',
        name: c.text || 'Untitled chapter',
      })),
    };
  }, [nodes, chapters]);

  const refFor = (node) => (node.type === 'chapter' ? `chapter:${node._id}` : `node:${node._id}`);

  // Measure after every layout that could have moved something: a changed node
  // list, a changed label, a resize of the diagram itself.
  const measure = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const stageBox = stage.getBoundingClientRect();
    const next = new Map();
    nodeRefs.current.forEach((el, ref) => {
      if (!el || !el.isConnected) return;
      const box = el.getBoundingClientRect();
      next.set(ref, {
        // Relative to the scrolled content, not the viewport, so the curves
        // stay attached when the diagram is scrolled.
        left: box.left - stageBox.left + stage.scrollLeft,
        right: box.right - stageBox.left + stage.scrollLeft,
        y: box.top - stageBox.top + stage.scrollTop + box.height / 2,
      });
    });
    setMeasured(next);
    setCanvas({ w: stage.scrollWidth, h: stage.scrollHeight });
  }, []);

  useLayoutEffect(() => {
    measure();
    const stage = stageRef.current;
    if (!stage || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(stage);
    return () => ro.disconnect();
  }, [measure, nodes, chapters]);

  const lines = useMemo(
    () => flowLines(edges, measured, selectedRef),
    [edges, measured, selectedRef],
  );

  const connectedRefs = useMemo(() => {
    if (!focusRef) return null;
    const set = new Set();
    edges.forEach((e) => {
      if (e.from === focusRef) set.add(e.to);
      else if (e.to === focusRef) set.add(e.from);
    });
    return set;
  }, [edges, focusRef]);

  return (
    <Box
      ref={stageRef}
      data-testid="flow-diagram"
      onScroll={measure}
      onPointerDown={(e) => { if (e.target === e.currentTarget) onSelect?.(null); }}
      sx={{
        position: 'relative', minHeight: '74vh', overflow: 'auto',
        backgroundColor: CB.white,
        borderRight: { md: `1px solid ${CB.lineSoft}` },
      }}
    >
      <svg
        aria-hidden="true"
        width={canvas.w || '100%'}
        height={canvas.h || '100%'}
        style={{
          position: 'absolute', top: 0, left: 0,
          width: canvas.w || '100%',
          height: canvas.h || '100%',
          pointerEvents: 'none', zIndex: 1,
        }}
      >
        {lines.map((line) => {
          const faded = Boolean(focusRef) && !line.involved;
          return (
            <path
              key={line.id}
              d={line.d}
              fill="none"
              stroke={line.involved ? CB.goldDeep : CB.lineSoft}
              strokeWidth={line.involved ? 2 : 1.5}
              opacity={faded ? 0.15 : 1}
            >
              {line.label ? <title>{line.label}</title> : null}
            </path>
          );
        })}
      </svg>

      {nodes.length === 0 ? emptyState : null}

      <Box sx={{
        position: 'relative', zIndex: 2,
        display: 'flex', justifyContent: 'space-around', gap: 2,
        px: 3, py: 4.5,
        // 720 keeps four columns readable when there is room for them. Below
        // 900 it is dropped and the columns shrink instead: holding a minimum
        // width on a phone pushes two of the four off the side of the screen,
        // where nothing tells you they are there.
        minWidth: 720,
        [BP.panelFloats]: { minWidth: 0, gap: 1, px: 1.5 },
      }}>
        {COLUMNS.map(({ key, label }) => (
          <Box key={key} sx={{
            display: 'flex', flexDirection: 'column', gap: 2.75,
            width: 200, flexShrink: 0,
            [BP.panelFloats]: { width: 'auto', flex: '1 1 0', minWidth: 96 },
          }}>
            <Typography sx={{
              fontFamily: FONT.mono, fontSize: 10, letterSpacing: '.1em',
              textTransform: 'uppercase', textAlign: 'center',
              color: TYPE_COLOR[key] || CB.gray,
            }}>
              {label}
            </Typography>

            {(columns[key] || []).length === 0 ? (
              <Typography sx={{
                fontFamily: FONT.mono, fontSize: 10, color: CB.gray,
                textAlign: 'center', opacity: 0.6,
              }}>
                —
              </Typography>
            ) : null}

            {(columns[key] || []).map((node) => {
              const ref = refFor(node);
              const isSelected = ref === selectedRef;
              const dimmed = Boolean(connectedRefs) && !isSelected && !connectedRefs.has(ref);
              const skin = NODE_SKIN[node.type] || NODE_SKIN.chapter;
              return (
                <Box
                  key={ref}
                  ref={(el) => {
                    if (el) nodeRefs.current.set(ref, el);
                    else nodeRefs.current.delete(ref);
                  }}
                  role={node.type === 'chapter' ? undefined : 'button'}
                  tabIndex={node.type === 'chapter' ? undefined : 0}
                  aria-pressed={node.type === 'chapter' ? undefined : isSelected}
                  onClick={() => { if (node.type !== 'chapter') onSelect?.(node._id); }}
                  onKeyDown={(e) => {
                    if (node.type === 'chapter') return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect?.(node._id);
                    }
                  }}
                  sx={{
                    backgroundColor: skin.bg,
                    border: `1.5px solid ${isSelected ? CB.gold : skin.border}`,
                    borderRadius: '6px',
                    px: 1.75, py: 1.5,
                    // A chapter is not editable from here — it is a heading in
                    // the manuscript — so it does not offer a click.
                    cursor: node.type === 'chapter' ? 'default' : 'pointer',
                    opacity: dimmed ? 0.3 : 1,
                    boxShadow: isSelected ? `0 0 0 3px ${CB.gold}2E` : 'none',
                    transition: 'opacity .2s ease, border-color .2s ease, transform .2s ease',
                    '&:hover': node.type === 'chapter'
                      ? undefined
                      : { transform: 'translateX(3px)' },
                    '&:focus-visible': { outline: `2px solid ${CB.gold}`, outlineOffset: '2px' },
                  }}
                >
                  <Typography sx={{
                    fontFamily: FONT.serif, fontWeight: 600, fontSize: 14, color: CB.black,
                    overflowWrap: 'anywhere',
                  }}>
                    {node.name || 'Untitled'}
                  </Typography>
                  {node.desc ? (
                    <Typography sx={{
                      fontFamily: FONT.sans, fontSize: 11, color: CB.gray,
                      lineHeight: 1.4, mt: 0.4,
                      display: '-webkit-box', WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}>
                      {node.desc}
                    </Typography>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
